// QNNEAL Animation Configuration
// Global animation patterns using Framer Motion

export const springConfig = {
  type: "spring",
  stiffness: 300,
  damping: 30,
};

export const smoothSpring = {
  type: "spring",
  stiffness: 200,
  damping: 25,
};

// Page Transitions
export const pageTransition = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -20 },
  transition: { duration: 0.4, ease: [0.22, 1, 0.36, 1] },
};

export const pageSlide = {
  initial: { opacity: 0, x: 30 },
  animate: { opacity: 1, x: 0 },
  exit: { opacity: 0, x: -30 },
  transition: { duration: 0.35, ease: [0.22, 1, 0.36, 1] },
};

// Entrance Animations
export const fadeInUp = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] },
};

export const fadeInDown = {
  initial: { opacity: 0, y: -20 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] },
};

export const fadeInLeft = {
  initial: { opacity: 0, x: -30 },
  animate: { opacity: 1, x: 0 },
  transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] },
};

export const fadeInRight = {
  initial: { opacity: 0, x: 30 },
  animate: { opacity: 1, x: 0 },
  transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] },
};

export const scaleIn = {
  initial: { opacity: 0, scale: 0.9 },
  animate: { opacity: 1, scale: 1 },
  transition: { duration: 0.4, ease: [0.22, 1, 0.36, 1] },
};

export const scaleInBounce = {
  initial: { opacity: 0, scale: 0.8 },
  animate: { opacity: 1, scale: 1 },
  transition: { type: "spring", stiffness: 400, damping: 20 },
};

// Stagger Containers
export const staggerContainer = {
  animate: {
    transition: {
      staggerChildren: 0.08,
      delayChildren: 0.1,
    },
  },
};

export const staggerContainerFast = {
  animate: {
    transition: {
      staggerChildren: 0.05,
      delayChildren: 0.05,
    },
  },
};

export const staggerContainerSlow = {
  animate: {
    transition: {
      staggerChildren: 0.12,
      delayChildren: 0.15,
    },
  },
};

// Stagger Items
export const staggerItem = {
  initial: { opacity: 0, y: 15 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.4, ease: [0.22, 1, 0.36, 1] },
};

export const staggerItemScale = {
  initial: { opacity: 0, scale: 0.9 },
  animate: { opacity: 1, scale: 1 },
  transition: { duration: 0.35 },
};

// Hover Effects
export const hoverScale = {
  whileHover: { scale: 1.03 },
  whileTap: { scale: 0.98 },
  transition: { duration: 0.2 },
};

export const hoverScaleLarge = {
  whileHover: { scale: 1.05 },
  whileTap: { scale: 0.95 },
  transition: { duration: 0.2 },
};

export const hoverLift = {
  whileHover: { y: -4, boxShadow: "0 12px 30px -10px rgba(0,0,0,0.3)" },
  transition: { duration: 0.25 },
};

export const hoverGlow = (color = "#06B6D4") => ({
  whileHover: { 
    boxShadow: `0 0 20px ${color}40, 0 0 40px ${color}20`,
  },
  transition: { duration: 0.3 },
});

// Button Animations
export const buttonTap = {
  whileTap: { scale: 0.97 },
  transition: { duration: 0.1 },
};

export const buttonPulse = {
  animate: {
    scale: [1, 1.02, 1],
    boxShadow: [
      "0 0 0 0 rgba(6, 182, 212, 0.4)",
      "0 0 0 10px rgba(6, 182, 212, 0)",
      "0 0 0 0 rgba(6, 182, 212, 0)",
    ],
  },
  transition: {
    duration: 1.5,
    repeat: Infinity,
    ease: "easeInOut",
  },
};

// Loading Animations
export const shimmer = {
  animate: {
    backgroundPosition: ["200% 0", "-200% 0"],
  },
  transition: {
    duration: 1.5,
    repeat: Infinity,
    ease: "linear",
  },
};

export const spin = {
  animate: { rotate: 360 },
  transition: { duration: 1, repeat: Infinity, ease: "linear" },
};

export const pulse = {
  animate: { opacity: [0.5, 1, 0.5] },
  transition: { duration: 1.5, repeat: Infinity, ease: "easeInOut" },
};

// Modal & Dialog Animations
export const modalBackdrop = {
  initial: { opacity: 0 },
  animate: { opacity: 1 },
  exit: { opacity: 0 },
  transition: { duration: 0.2 },
};

export const modalContent = {
  initial: { opacity: 0, scale: 0.95, y: 10 },
  animate: { opacity: 1, scale: 1, y: 0 },
  exit: { opacity: 0, scale: 0.95, y: 10 },
  transition: { duration: 0.25, ease: [0.22, 1, 0.36, 1] },
};

// Notification/Toast Animations
export const slideInFromTop = {
  initial: { opacity: 0, y: -20, scale: 0.95 },
  animate: { opacity: 1, y: 0, scale: 1 },
  exit: { opacity: 0, y: -20, scale: 0.95 },
  transition: { duration: 0.3, ease: [0.22, 1, 0.36, 1] },
};

export const slideInFromRight = {
  initial: { opacity: 0, x: 100 },
  animate: { opacity: 1, x: 0 },
  exit: { opacity: 0, x: 100 },
  transition: { duration: 0.3, ease: [0.22, 1, 0.36, 1] },
};

// Chart Animations
export const chartGrow = {
  initial: { scaleY: 0, originY: 1 },
  animate: { scaleY: 1 },
  transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1], delay: 0.2 },
};

export const lineDraw = {
  initial: { pathLength: 0, opacity: 0 },
  animate: { pathLength: 1, opacity: 1 },
  transition: { duration: 1.2, ease: [0.22, 1, 0.36, 1] },
};

// Number Counter Animation
export const counterAnimation = (from, to, duration = 1) => ({
  initial: { value: from },
  animate: { value: to },
  transition: { duration, ease: "easeOut" },
});

// DAG/Node Animations
export const nodeAppear = {
  initial: { opacity: 0, scale: 0.5 },
  animate: { opacity: 1, scale: 1 },
  transition: { type: "spring", stiffness: 300, damping: 20 },
};

export const edgeDraw = {
  initial: { pathLength: 0, opacity: 0 },
  animate: { pathLength: 1, opacity: 1 },
  transition: { duration: 0.6, ease: "easeOut" },
};

// Hero Section Animations
export const heroText = {
  initial: { opacity: 0, y: 30 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] },
};

export const heroBadge = {
  initial: { opacity: 0, scale: 0.8 },
  animate: { opacity: 1, scale: 1 },
  transition: { duration: 0.5, delay: 0.2 },
};

export const heroButton = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.5, delay: 0.4 },
};

// Floating Animation
export const float = {
  animate: {
    y: [0, -10, 0],
  },
  transition: {
    duration: 3,
    repeat: Infinity,
    ease: "easeInOut",
  },
};

// Quantum Glow Effect
export const quantumGlow = {
  animate: {
    boxShadow: [
      "0 0 20px rgba(6, 182, 212, 0.3), 0 0 40px rgba(124, 58, 237, 0.1)",
      "0 0 30px rgba(6, 182, 212, 0.5), 0 0 60px rgba(124, 58, 237, 0.2)",
      "0 0 20px rgba(6, 182, 212, 0.3), 0 0 40px rgba(124, 58, 237, 0.1)",
    ],
  },
  transition: {
    duration: 2,
    repeat: Infinity,
    ease: "easeInOut",
  },
};

// Export all as default object for convenience
const animations = {
  pageTransition,
  pageSlide,
  fadeInUp,
  fadeInDown,
  fadeInLeft,
  fadeInRight,
  scaleIn,
  scaleInBounce,
  staggerContainer,
  staggerContainerFast,
  staggerContainerSlow,
  staggerItem,
  staggerItemScale,
  hoverScale,
  hoverScaleLarge,
  hoverLift,
  hoverGlow,
  buttonTap,
  buttonPulse,
  shimmer,
  spin,
  pulse,
  modalBackdrop,
  modalContent,
  slideInFromTop,
  slideInFromRight,
  chartGrow,
  lineDraw,
  nodeAppear,
  edgeDraw,
  heroText,
  heroBadge,
  heroButton,
  float,
  quantumGlow,
};

export default animations;
